'use client';

import { useState, useEffect, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { Bot, X } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import AgentChat from '@/components/operations/AgentChat';
import { authFetch } from '@/lib/authFetch';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3003';
const LAST_SEEN_KEY = 'agent_messages_last_seen_main';
const POLL_INTERVAL_MS = 30_000;

interface CrmCustomer {
  id: string;
  name: string;
  email?: string;
}

// Floating "talk to your agent" button + slide-out drawer.
// - Mounted globally so the agent is one tap away from any page
// - Hidden on /agent itself (no point doubling the UI) and on the workflow editor
// - Polls /api/agent-messages/since for unread count when closed
// - Embeds the same AgentChat the dashboard and /agent use, so all features
//   (paperclip, microphone, voice, images, mentions) are present
export default function AgentSlideOut() {
  const { tenant } = useAuth();
  const tenantId = tenant?.id;
  const pathname = usePathname();

  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [customers, setCustomers] = useState<CrmCustomer[]>([]);

  // Routes where the slide-out should NOT render (and polling should stop).
  // When isHidden=true the component returns null, so the polling useEffect
  // exits immediately via the `if (isHidden || open)` guard — no double-poll
  // when /agent is active with its own AgentChat already polling.
  const isHidden = pathname === '/agent'
    || pathname.startsWith('/agent/')
    || (pathname.startsWith('/workflows/') && pathname !== '/workflows')
    || pathname === '/auth'
    || pathname.startsWith('/auth/')
    || pathname === '/email'
    || pathname.startsWith('/email/');

  const loadCustomers = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/api/crm/customers`);
      if (res.ok) {
        const d = await res.json();
        setCustomers(Array.isArray(d) ? d : (d.customers || []));
      }
    } catch { /* network */ }
  }, []);

  useEffect(() => { if (open && customers.length === 0) loadCustomers(); }, [open, customers.length, loadCustomers]);

  // Poll unread agent messages when the drawer is closed
  useEffect(() => {
    if (isHidden || open) { setUnread(0); return; }
    let cancelled = false;

    async function check() {
      let lastSeen: string;
      try { lastSeen = localStorage.getItem(LAST_SEEN_KEY) || new Date(0).toISOString(); }
      catch { lastSeen = new Date(0).toISOString(); }
      try {
        const res = await authFetch(`${API_URL}/api/agent-messages/since?thread=main&ts=${encodeURIComponent(lastSeen)}`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const newAgentMsgs = (data.messages || []).filter((m: { role: string }) => m.role === 'agent');
        if (!cancelled) setUnread(newAgentMsgs.length);
      } catch { /* network */ }
    }

    check();
    const interval = setInterval(check, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isHidden, open]);

  // Mark as seen when opening the drawer (so the badge clears)
  useEffect(() => {
    if (!open) return;
    try { localStorage.setItem(LAST_SEEN_KEY, new Date().toISOString()); } catch { /* private */ }
    setUnread(0);
  }, [open]);

  // ESC closes the drawer
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (isHidden) return null;

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 bg-black/30 z-40 transition-opacity"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Drawer */}
      <aside
        className="fixed top-0 right-0 h-screen z-50 flex flex-col transition-transform duration-300"
        style={{
          width: 'min(440px, 100vw)',
          background: 'var(--bg-card)',
          boxShadow: open ? '-12px 0 32px rgba(0,0,0,0.18)' : 'none',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
        }}
        aria-hidden={!open}
      >
        {/* Header */}
        <div className="shrink-0 flex items-center gap-3 px-5 py-4">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
            style={{ background: 'rgba(0,194,168,0.1)' }}
          >
            <Bot className="w-5 h-5 text-[#00C2A8]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[15px] font-semibold text-[--text-primary] tracking-tight leading-tight">Agent</p>
            <p className="text-[11px] text-[--text-tertiary]">Always-on, knows your business</p>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="p-1.5 rounded-lg hover:bg-[--bg-tint] text-[--text-secondary] transition-colors"
            aria-label="Close agent panel"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Chat body */}
        <div className="flex-1 min-h-0 px-4 pb-4">
          {open && (
            <AgentChat
              customers={customers}
              onAgentAction={loadCustomers}
              tenantId={tenantId}
            />
          )}
        </div>
      </aside>

      {/* Floating launcher button — desktop only. On mobile, the bottom tab
          bar already has an Agent tab; a floating launcher would be redundant
          and would crash into the tab bar at bottom-6. */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="hidden md:block fixed bottom-6 right-6 z-30 group"
          aria-label="Open agent chat"
        >
          <span
            className="relative flex items-center justify-center w-14 h-14 rounded-full text-white transition-transform group-hover:scale-105 group-active:scale-95"
            style={{
              background: 'linear-gradient(135deg, #00C2A8, #00a892)',
              boxShadow: '0 12px 24px -8px rgba(0,194,168,0.55), 0 4px 12px rgba(0,0,0,0.18)',
            }}
          >
            <Bot className="w-6 h-6" strokeWidth={2.25} />
            {unread > 0 && (
              <span
                className="absolute -top-1 -right-1 min-w-[20px] h-[20px] px-1.5 rounded-full text-[10px] font-bold flex items-center justify-center leading-none"
                style={{ background: '#ef4444', color: '#fff', border: '2px solid var(--bg-main)' }}
              >
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </span>
        </button>
      )}
    </>
  );
}
